import React, { useEffect, useMemo, useState } from 'react';
import './ShareJobDialog.less';
import Button from '../../../components/envago/Button/Button';
import Dialog from '../../../components/envago/Dialog/Dialog';
import { JobType } from '../../../redux/jobs/jobs.types';
import LabeledList from '../../../components/envago/LabeledList/LabeledList';
import OptionalLabel from '../../../components/envago/OptionalLabel/OptionalLabel';
import Input from '../../../components/envago/Input/Input';
import Checkbox from '../../../components/envago/Checkbox/Checkbox';
import { useDispatch, useSelector } from 'react-redux';
import { sharesActions, sharesSelectors } from '../../../redux/shares';
import Loading from '../../../components/common/Loading/Loading';
import ClipboardText from '../../../components/envago/ClipboardText/ClipboardText';
import { useTranslation } from 'react-i18next';
import Collapsable from '../../../components/envago/Collapsable/Collapsable';
import ShareAccountEmail from './ShareAccountEmail';
import ExportExcel from '../ExportExcel/ExportExcel';
import { applicationSelectors } from '../../../redux/application';
import { assignmentPresetsActions, assignmentPresetsSelectors } from '../../../redux/assignmentPresets';
import Information, { InformationType } from '../../../components/envago/Information/Information';
import { AssignmentPresetType } from '../../../redux/login/login.types';

const ShareJobDialog = ({
    jobs,
    visible,
    onClose,
    preset,
}: {
    jobs: JobType[];
    visible: boolean;
    onClose: (open: boolean) => void;
    preset?: AssignmentPresetType;
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [shareName, setShareName] = useState('');
    const [reshare, setReshare] = useState(false);

    const [accountId, setAccountId] = useState('');

    useEffect(() => {
        /*
           this is necessary, through jobs might be set after creation
         */
        setAccountId(jobs.find((a) => a)?.login || '');
    }, [jobs]);

    const presetsEnabled = useSelector(applicationSelectors.isAssignmentPresetsAllowed);
    const existingPresets = useSelector(assignmentPresetsSelectors.getPresetsForAccount(accountId));
    const presetConflict = useSelector(assignmentPresetsSelectors.hasConflict);
    const lastSavedPresetId = useSelector(assignmentPresetsSelectors.getLastSavedId);

    useEffect(() => {
        if (accountId && presetsEnabled) {
            dispatch(assignmentPresetsActions.fetchPresets(accountId));
        }
    }, [accountId, presetsEnabled, dispatch]);

    const [savePreset, setSavePreset] = useState(false);
    const [presetMode, setPresetMode] = useState<'new' | 'overwrite'>('new');
    const [presetName, setPresetName] = useState('');
    const [overwriteId, setOverwriteId] = useState('');

    // Note: The close button will be enabled if the user has interacted with the primaryLoginSharingMethod or has opened the collapse to inspect other login methods.
    const [isCloseEnabled, setIsCloseEnabled] = useState(false);

    const isSending = useSelector(sharesSelectors.createIsSending);
    const createdAccount = useSelector(sharesSelectors.createGetAccount);
    const { excelExportAllowed, sendSubaccountViaEmailDisabled } = useSelector(applicationSelectors.getFeaturesConfig);

    const primaryLoginSharingMethod = useMemo(() => {
        if (sendSubaccountViaEmailDisabled) {
            return (
                <LabeledList
                    title={t('jobs.share.success.loginMethod.direct.title')}
                    description={t('jobs.share.success.loginMethod.direct.description')}
                    className={'primary-login-sharing-method'}
                >
                    <OptionalLabel label={t('jobs.share.labels.tokenLogin')}>
                        <ClipboardText
                            showText={`${window?.location?.origin}/m/${createdAccount?.token}`}
                            text={`${window?.location?.origin}/m/${createdAccount?.token}`}
                            onCopiedToClipboard={() => {
                                setIsCloseEnabled(true);
                            }}
                        />
                    </OptionalLabel>
                </LabeledList>
            );
        }

        return (
            <ShareAccountEmail
                onEmailsSend={(isEmailsSend) => setIsCloseEnabled(isEmailsSend)}
                onEmailsShared={(sentEmails) => {
                    // Only patch the preset that was actually saved during create-access. If the
                    // choice was incomplete (no name in new mode / no target in overwrite mode),
                    // nothing was saved (lastSavedPresetId stays undefined) and this is a no-op.
                    if (!(presetsEnabled && savePreset && lastSavedPresetId)) return;

                    const isOverwrite = presetMode === 'overwrite';
                    if (isOverwrite && !overwriteId) return;
                    if (!isOverwrite && !presetName.trim()) return;

                    dispatch(
                        assignmentPresetsActions.updatePreset(accountId, lastSavedPresetId, {
                            name: isOverwrite ? existingPresets.find((p) => p.id === overwriteId)?.name ?? presetName : presetName,
                            jobIds: jobs.flatMap((job) => job.id),
                            receiver: { name: shareName, reshare, emails: sentEmails },
                        }),
                    );
                }}
                accountId={accountId}
                initialEmails={preset?.receiver?.emails ?? []}
            />
        );
    }, [
        createdAccount,
        sendSubaccountViaEmailDisabled,
        accountId,
        t,
        dispatch,
        presetsEnabled,
        savePreset,
        lastSavedPresetId,
        presetMode,
        presetName,
        overwriteId,
        existingPresets,
        jobs,
        shareName,
        reshare,
        preset,
    ]);

    useEffect(() => {
        dispatch(sharesActions.createSubAccountReset());
        setShareName(preset?.receiver?.name ?? '');
        setReshare(preset?.receiver?.reshare ?? false);
        setSavePreset(false);
        setPresetMode(preset ? 'overwrite' : 'new');
        setPresetName('');
        setOverwriteId(preset?.id ?? '');
        dispatch(assignmentPresetsActions.resetStatus());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, preset]);

    return (
        <>
            <Dialog
                title={t('jobs.share.title')}
                onClose={onClose}
                closable={true}
                content={
                    <div className="max-w-screen-lg">
                        {!isSending && !createdAccount && (
                            <div>
                                <LabeledList>
                                    <OptionalLabel label={t('jobs.share.labels.accessName')} description={t('jobs.share.descriptions.accessName')}>
                                        <Input
                                            placeholder={t('jobs.share.placeholders.name')}
                                            value={shareName}
                                            autoFocus={true}
                                            onChange={setShareName}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') e.currentTarget.blur();
                                            }}
                                        />
                                    </OptionalLabel>

                                    <OptionalLabel label={t('jobs.share.labels.additionalOptions')} description={t('jobs.share.descriptions.reshareAllowed')}>
                                        <Checkbox value={reshare} onChange={setReshare} className={'mb-3'} label={t('jobs.share.labels.reshareAllowed')} />
                                    </OptionalLabel>

                                    {presetsEnabled && (
                                        <OptionalLabel>
                                            <Checkbox
                                                value={savePreset}
                                                onChange={setSavePreset}
                                                className={'mb-3'}
                                                label={t('jobs.share.assignmentPresets.saveCheckbox')}
                                            />
                                            {savePreset && (
                                                <div className={'space-y-2'}>
                                                    <div role={'radiogroup'}>
                                                        <label className={'flex items-center gap-2 mb-1'}>
                                                            <input
                                                                type={'radio'}
                                                                name={'assignmentPresetMode'}
                                                                checked={presetMode === 'new'}
                                                                onChange={() => setPresetMode('new')}
                                                                className={'h-4 w-4 text-primary'}
                                                            />
                                                            <span className={'text-sm'}>{t('jobs.share.assignmentPresets.modeNew')}</span>
                                                        </label>
                                                        {presetMode === 'new' && (
                                                            <Input
                                                                placeholder={t('jobs.share.assignmentPresets.nameLabel')}
                                                                value={presetName}
                                                                onChange={setPresetName}
                                                            />
                                                        )}
                                                        {existingPresets.length > 0 && (
                                                            <>
                                                                <label className={'flex items-center gap-2 mb-1'}>
                                                                    <input
                                                                        type={'radio'}
                                                                        name={'assignmentPresetMode'}
                                                                        checked={presetMode === 'overwrite'}
                                                                        onChange={() => setPresetMode('overwrite')}
                                                                        className={'h-4 w-4 text-primary'}
                                                                    />
                                                                    <span className={'text-sm'}>{t('jobs.share.assignmentPresets.modeOverwrite')}</span>
                                                                </label>
                                                                {presetMode === 'overwrite' && (
                                                                    <select
                                                                        aria-label={t('jobs.share.assignmentPresets.selectExisting')}
                                                                        value={overwriteId}
                                                                        onChange={(e) => setOverwriteId(e.target.value)}
                                                                        className={'form-select rounded-md border w-full h-8 leading-8 py-0 text-black mt-2 mb-2'}
                                                                    >
                                                                        <option value={''}>{t('jobs.share.assignmentPresets.selectExisting')}</option>
                                                                        {existingPresets.map((preset) => (
                                                                            <option key={preset.id} value={preset.id}>
                                                                                {preset.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    {presetConflict && (
                                                        <Information type={InformationType.ERROR}>
                                                            {t('jobs.share.assignmentPresets.nameConflict')}
                                                        </Information>
                                                    )}
                                                </div>
                                            )}
                                        </OptionalLabel>
                                    )}

                                    <OptionalLabel>
                                        <Button
                                            block={true}
                                            primary={true}
                                            disabled={
                                                !shareName ||
                                                (savePreset && presetMode === 'overwrite' && !overwriteId) ||
                                                (savePreset && presetMode === 'new' && !presetName.trim())
                                            }
                                            onClick={() => {
                                                dispatch(
                                                    sharesActions.createSubAccount(
                                                        {
                                                            allowToReShare: reshare,
                                                            name: shareName,
                                                            jobIds: jobs.flatMap((job) => job.id),
                                                        },
                                                        jobs.find((job) => !!job)?.login || '',
                                                    ),
                                                );

                                                if (presetsEnabled && savePreset) {
                                                    if (presetMode === 'overwrite') {
                                                        // Only overwrite when a target preset was actually chosen; an
                                                        // incomplete selection skips the (optional) preset save entirely.
                                                        if (overwriteId) {
                                                            dispatch(
                                                                assignmentPresetsActions.updatePreset(accountId, overwriteId, {
                                                                    name: existingPresets.find((p) => p.id === overwriteId)?.name ?? presetName,
                                                                    jobIds: jobs.flatMap((job) => job.id),
                                                                    receiver: {
                                                                        name: shareName,
                                                                        reshare,
                                                                        emails: existingPresets.find((p) => p.id === overwriteId)?.receiver.emails ?? [],
                                                                    },
                                                                }),
                                                            );
                                                        }
                                                    } else if (presetName.trim()) {
                                                        // New mode: only create when a non-empty name was entered.
                                                        dispatch(
                                                            assignmentPresetsActions.createPreset(accountId, {
                                                                name: presetName,
                                                                jobIds: jobs.flatMap((job) => job.id),
                                                                receiver: { name: shareName, reshare, emails: [] },
                                                            }),
                                                        );
                                                    }
                                                }
                                            }}
                                        >
                                            {t('jobs.share.buttons.createAccess')}
                                        </Button>
                                    </OptionalLabel>
                                </LabeledList>
                            </div>
                        )}
                        {isSending && <Loading text={t('jobs.share.sending')} />}
                        {createdAccount && (
                            <div className="max-w-screen-lg">
                                <div>
                                    <p>
                                        {t('jobs.share.success.summary', {
                                            name: shareName,
                                            count: jobs.length,
                                        })}
                                    </p>
                                    <p>{t('jobs.share.success.message')}</p>
                                </div>

                                {presetsEnabled && savePreset && presetConflict && (
                                    <Information type={InformationType.ERROR} className={'mb-4'}>
                                        {t('jobs.share.assignmentPresets.nameConflict')}
                                    </Information>
                                )}

                                {primaryLoginSharingMethod}

                                <Collapsable
                                    onChange={(visible) => {
                                        setIsCloseEnabled(visible);
                                    }}
                                    className={'mt-6'}
                                    triggerText={t('jobs.share.success.loginMethod.triggerText')}
                                >
                                    {!sendSubaccountViaEmailDisabled && (
                                        <LabeledList
                                            title={t('jobs.share.success.loginMethod.direct.title')}
                                            description={t('jobs.share.success.loginMethod.direct.description')}
                                            className={'mt-2'}
                                        >
                                            <OptionalLabel label={t('jobs.share.labels.tokenLogin')}>
                                                <ClipboardText
                                                    showText={`${window?.location?.origin}/m/${createdAccount.token}`}
                                                    text={`${window?.location?.origin}/m/${createdAccount.token}`}
                                                />
                                            </OptionalLabel>
                                        </LabeledList>
                                    )}

                                    <LabeledList
                                        title={t('jobs.share.success.loginMethod.web.title')}
                                        description={t('jobs.share.success.loginMethod.web.description')}
                                        className={'mt-2'}
                                    >
                                        <OptionalLabel label={t('jobs.share.labels.webLogin')}>
                                            <ClipboardText showText={`${window?.location?.origin}`} text={`${window?.location?.origin}`} />
                                        </OptionalLabel>
                                        <OptionalLabel label={t('jobs.share.labels.username')}>
                                            <ClipboardText text={createdAccount.username} />
                                        </OptionalLabel>
                                        <OptionalLabel label={t('jobs.share.labels.password')}>
                                            <ClipboardText text={createdAccount.password} />
                                        </OptionalLabel>
                                    </LabeledList>

                                    {excelExportAllowed && (
                                        <LabeledList
                                            title={t('management.accounts.share.exportXlsx.title')}
                                            description={t('management.accounts.share.exportXlsx.description')}
                                            className={'mt-2'}
                                        >
                                            <OptionalLabel label={t('management.accounts.share.exportXlsx.label')}>
                                                <ExportExcel jobs={jobs} />
                                            </OptionalLabel>
                                        </LabeledList>
                                    )}
                                </Collapsable>
                                <LabeledList className={'mt-4'}>
                                    <OptionalLabel>
                                        <Button
                                            disabled={!isCloseEnabled}
                                            block={true}
                                            onClick={() => {
                                                onClose(false);
                                            }}
                                        >
                                            {t('jobs.share.buttons.close')}
                                        </Button>
                                    </OptionalLabel>
                                </LabeledList>
                            </div>
                        )}
                    </div>
                }
                visible={visible}
            />
        </>
    );
};

export default ShareJobDialog;
